import type { AssignmentTarget } from "@/store/types";
import type { AssignmentFold } from "./assignments";
import { type DispatchTableRow, rowAssignments } from "./dispatchTableRow";
import type { DispatchGroup } from "./groupDispatchRows";
import type { GroupedDispatchRows } from "./useDispatchQuery";

/** The shelf's group key: every row handed to the viewer. */
export const ASSIGNED_TO_ME_GROUP_KEY = "_assignedToMe";
/** The team queue's group key: every row handed to the current view's team. */
export const ASSIGNED_TO_TEAM_GROUP_KEY = "_assignedToTeam";
/** The group holding the view's own rows when the view has no grouping of its own. */
export const REST_GROUP_KEY = "_rest";

/** A row's identity across group copies: grouping files a multi-valued row under `<id>@<group>`. */
function baseRowKey(row: DispatchTableRow): string {
  return row.kind === "message" ? row.item.id : row.driver.workspaceMembershipId;
}

/** The rows of a grouping, group by group, with collapsed groups empty: grouping counts their rows but leaves them out. */
function rowsPerGroup(groups: readonly DispatchGroup[], grouped: readonly DispatchTableRow[]): DispatchTableRow[][] {
  let next = 0;
  return groups.map((group) => {
    if (group.collapsed) return [];
    const rows = grouped.slice(next, next + group.count);
    next += group.count;
    return rows;
  });
}

/**
 * The view's rows with the reassignment prototype's shelf and team queue pinned above them.
 *
 * A row is "mine" when an open assignment on it (the message row's target, or any on a driver row's channel) names the
 * viewer, and "the team's" when one names the current view and none names the viewer. Both come from every row of the
 * grain, so the view's filters, time controls, and search never hide them. The groups are `Assigned to you`, then
 * `Assigned to <view name>`, then the view's own groups with those rows taken out, or one `Everything else` group
 * when the view groups nothing. Returns `null` when neither set has a row, leaving the table as it was.
 *
 * Every group comes back unfolded; {@link foldShelfGroups} applies the view state's collapsed set to the three keys
 * this adds.
 */
export function withAssignmentShelf(input: {
  /** Every row of the grain after sorting, before the view's filters. */
  sorted: readonly DispatchTableRow[];
  /** The rows the view shows, grouped or not, as useDispatchQuery computed them. */
  shown: GroupedDispatchRows | { groups: null; rows: readonly DispatchTableRow[] };
  fold: AssignmentFold;
  me: string;
  view: { id: string; name: string };
}): GroupedDispatchRows | null {
  const { sorted, shown, fold, me, view } = input;
  if (fold.byTarget.size === 0) return null;
  const names = (row: DispatchTableRow, matches: (target: AssignmentTarget) => boolean) =>
    rowAssignments(row, fold).some((open) => matches(open.assignee));

  const mine = sorted.filter((row) => names(row, (target) => target.kind === "member" && target.membershipId === me));
  const mineKeys = new Set(mine.map(baseRowKey));
  const team = sorted.filter(
    (row) =>
      !mineKeys.has(baseRowKey(row)) && names(row, (target) => target.kind === "team" && target.viewId === view.id),
  );
  if (mine.length === 0 && team.length === 0) return null;

  const shelved = new Set([...mineKeys, ...team.map(baseRowKey)]);
  const keep = (row: DispatchTableRow) => !shelved.has(baseRowKey(row));
  const groups: DispatchGroup[] = [];
  const rows: DispatchTableRow[] = [];
  const push = (group: Omit<DispatchGroup, "count" | "collapsed">, groupRows: readonly DispatchTableRow[]) => {
    groups.push({ ...group, count: groupRows.length, collapsed: false });
    rows.push(...groupRows);
  };

  if (mine.length > 0) push({ key: ASSIGNED_TO_ME_GROUP_KEY, label: "Assigned to you" }, mine);
  if (team.length > 0) push({ key: ASSIGNED_TO_TEAM_GROUP_KEY, label: `Assigned to ${view.name}` }, team);
  const viewGroups = shown.groups;
  if (viewGroups === null) {
    push({ key: REST_GROUP_KEY, label: "Everything else" }, shown.rows.filter(keep));
    return { groups, rows };
  }
  const perGroup = rowsPerGroup(viewGroups, shown.rows);
  viewGroups.forEach((group, index) => {
    // A collapsed group's rows are not at hand, so its count stays as grouping made it.
    if (group.collapsed) {
      groups.push(group);
      return;
    }
    const groupRows = perGroup[index].filter(keep);
    groups.push({ ...group, count: groupRows.length });
    rows.push(...groupRows);
  });
  return { groups, rows };
}

/** The shelf's own groups folded as the view state's collapsed set says: their rows dropped, their counts kept. */
export function foldShelfGroups(shelf: GroupedDispatchRows, collapsed: ReadonlySet<string>): GroupedDispatchRows {
  const added = [ASSIGNED_TO_ME_GROUP_KEY, ASSIGNED_TO_TEAM_GROUP_KEY, REST_GROUP_KEY];
  if (!added.some((key) => collapsed.has(key))) return shelf;
  const rows: DispatchTableRow[] = [];
  let next = 0;
  const groups = shelf.groups.map((group) => {
    if (group.collapsed) return group;
    const groupRows = shelf.rows.slice(next, next + group.count);
    next += group.count;
    if (added.includes(group.key) && collapsed.has(group.key)) return { ...group, collapsed: true };
    rows.push(...groupRows);
    return group;
  });
  return { groups, rows };
}
